/**
 * LOGOS v5.0 — Protocol Engine (Layer 8)
 * SRS-EXPVAL-001: Empirical Validation Protocol
 * 
 * This module is ADDITIVE — it does NOT modify any existing core functions.
 * It CONSUMES outputs from the 7 existing layers (L7→L1→L2→L3→L4→L5→L6)
 * and computes protocol-specific metrics for the empirical validation study.
 * 
 * Functions: 11 new (functions #38-48 in the v5.0 inventory)
 */

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface EmergenceEvent {
  id: string;
  userId: string;
  createdAt: string;
  category: EmergenceCategory | null;
  hadEmergence: boolean;
  impact: number;
  description?: string;
  goalAssociation?: string;
  stateSnapshot: Record<string, number>;
  ersAtEvent: number; // DEPRECATED: use lersAtEvent
  lersAtEvent?: number;
  lambdaAtEvent: number;
  watchBiometrics?: WatchBiometrics;
  studyDay?: number;
  reportLatencyS?: number;
}

export type EmergenceCategory =
  | 'E1_insight'
  | 'E2_conexion'
  | 'E3_oportunidad'
  | 'E4_solucion'
  | 'E5_flujo'
  | 'E6_sincronicidad';

export interface WatchBiometrics {
  hrv?: number;
  hr?: number;
  spo2?: number;
  sleepHours?: number;
  steps?: number;
  exerciseMin?: number;
}

export interface H1Stats {
  contingencyTable: {
    a: number; // High ERS + Emergence
    b: number; // High ERS + No Emergence
    c: number; // Low ERS + Emergence
    d: number; // Low ERS + No Emergence
  };
  oddsRatio: number;
  orCI: [number, number]; // 95% CI
  pValue: number;
  bf10: number;
  totalEvents: number;
  power: number;
  significant: boolean; // OR > 2.0 AND p < 0.05
}

export interface CalibrationResult {
  weights: Record<string, number>;
  epoch: number;
  waicScore: number;
  waicSE: number;
  deltaWaic: number;
  safetyCheck: {
    monotonicity: boolean;
    impedance: boolean;
    lyapunov: boolean;
  };
  passed: boolean;
}

export interface WilsonCIResult {
  proportion: number;
  lower: number;
  upper: number;
  n: number;
}

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

export const LERS_THRESHOLD = 0.50;
export const ERS_THRESHOLD = LERS_THRESHOLD; // backward compatibility alias — DEPRECATED

export const LERS_WEIGHTS = {
  lambda: 0.35,
  viability: 0.25,
  coherence: 0.25,
  entropy: 0.15, // note: uses (1 - S)
} as const;
export const ERS_WEIGHTS = LERS_WEIGHTS; // backward compatibility alias — DEPRECATED

export const THEORETICAL_WEIGHTS: Record<string, number> = {
  faith: 0.17,
  gratitude: 0.12,
  purpose: 0.10,
  service: 0.08,
  peace: 0.08,
  meditation: 0.07,
  clarity: 0.06,
  love: 0.06,
  family: 0.05,
  community: 0.05,
  growth: 0.05,
  integrity: 0.04,
  nourishment: 0.03,
  taste_presence: 0.02,
  food_harmony: 0.01,
  gut_resonance: 0.01,
};
// Sum = 1.00 ✓

export const EMERGENCE_TAXONOMY = {
  E1_insight: {
    label: 'Insight',
    emoji: '💡',
    color: '#A855F7',
    prompt: '¿Tuviste una idea que conectó conceptos no relacionados?',
    signature: 'Penrose C>0.65 + Hoffman clearance',
  },
  E2_conexion: {
    label: 'Conexión',
    emoji: '🤝',
    color: '#06B6D4',
    prompt: '¿Encontraste a la persona correcta en el momento justo?',
    signature: 'Levin morpho + Watson attractor',
  },
  E3_oportunidad: {
    label: 'Oportunidad',
    emoji: '🌟',
    color: '#F59E0B',
    prompt: '¿Apareció algo valioso que no buscabas activamente?',
    signature: 'Friston surprise↓ + ERS>0.50',
  },
  E4_solucion: {
    label: 'Solución',
    emoji: '🔓',
    color: '#10B981',
    prompt: '¿Un problema se resolvió sin esfuerzo deliberado?',
    signature: 'Watson landscape + Λ gradient',
  },
  E5_flujo: {
    label: 'Flujo',
    emoji: '🌊',
    color: '#3B82F6',
    prompt: '¿Estuviste productivo sin esfuerzo consciente (≥2h)?',
    signature: 'Penrose C>0.70 + S<0.30',
  },
  E6_sincronicidad: {
    label: 'Sincronicidad',
    emoji: '✨',
    color: '#EC4899',
    prompt: '¿Ocurrió coincidencia significativa ligada a una meta?',
    signature: 'GAF>2.0 + ERS>0.50',
  },
} as const;

// ═══════════════════════════════════════════════════════════════
// FUNCTION #38: computeLERS (Logos Emergence Readiness Score)
// Renamed from computeERS to avoid collision with KAIRA ERS
// Emergence Readiness Score — composite predictor [0,1]
// ═══════════════════════════════════════════════════════════════

export function computeLERS(
  lambda: number,
  viability: number,
  coherence: number,
  entropy: number
): number {
  const raw =
    LERS_WEIGHTS.lambda * lambda +
    LERS_WEIGHTS.viability * viability +
    LERS_WEIGHTS.coherence * coherence +
    LERS_WEIGHTS.entropy * (1 - entropy);
  return Math.max(0, Math.min(1, raw));
}
// Backward compatibility alias — DEPRECATED
export const computeERS = computeLERS;

// ═══════════════════════════════════════════════════════════════
// FUNCTION #39: computeOddsRatio
// 2×2 contingency table → OR + 95% CI + p-value
// ═══════════════════════════════════════════════════════════════

export function computeOddsRatio(
  a: number, // High ERS + Emergence
  b: number, // High ERS + No Emergence
  c: number, // Low ERS + Emergence
  d: number  // Low ERS + No Emergence
): { or: number; ci: [number, number]; p: number; se: number } {
  // Haldane correction for zero cells
  const aa = a || 0.5;
  const bb = b || 0.5;
  const cc = c || 0.5;
  const dd = d || 0.5;

  const or = (aa * dd) / (bb * cc);
  const se = Math.sqrt(1 / aa + 1 / bb + 1 / cc + 1 / dd);
  const logOR = Math.log(or);
  const z = 1.96; // 95% CI

  const ciLower = Math.exp(logOR - z * se);
  const ciUpper = Math.exp(logOR + z * se);

  // Chi-squared test for independence
  const n = a + b + c + d;
  if (n === 0) {
    return { or, ci: [ciLower, ciUpper], p: 1.0, se };
  }
  const expected_a = ((a + b) * (a + c)) / n;
  const expected_b = ((a + b) * (b + d)) / n;
  const expected_c = ((c + d) * (a + c)) / n;
  const expected_d = ((c + d) * (b + d)) / n;

  const chi2 =
    Math.pow(a - expected_a, 2) / (expected_a || 1) +
    Math.pow(b - expected_b, 2) / (expected_b || 1) +
    Math.pow(c - expected_c, 2) / (expected_c || 1) +
    Math.pow(d - expected_d, 2) / (expected_d || 1);

  // p-value from chi-squared with 1 df (approximation)
  const p = 1 - normalCDF(Math.sqrt(Math.max(0, chi2)));
  // Two-tailed
  const pValue = 2 * Math.min(p, 1 - p);

  return { or, ci: [ciLower, ciUpper], p: pValue, se };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #40: wilsonCI
// Wilson Score confidence interval for proportions
// ═══════════════════════════════════════════════════════════════

export function wilsonCI(
  successes: number,
  total: number,
  confidence: number = 0.95
): WilsonCIResult {
  if (total === 0) {
    return { proportion: 0, lower: 0, upper: 0, n: 0 };
  }

  const p = successes / total;
  const z = confidence === 0.95 ? 1.96 : confidence === 0.99 ? 2.576 : 1.645;
  const z2 = z * z;
  const n = total;

  const denominator = 1 + z2 / n;
  const center = p + z2 / (2 * n);
  const margin = z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n));

  return {
    proportion: p,
    lower: Math.max(0, (center - margin) / denominator),
    upper: Math.min(1, (center + margin) / denominator),
    n: total,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #41: computeBF10
// Bayes Factor (BIC approximation) — evidence for H1 vs H0
// ═══════════════════════════════════════════════════════════════

export function computeBF10(
  a: number,
  b: number,
  c: number,
  d: number
): number {
  const n = a + b + c + d;
  if (n === 0) return 1;

  // BIC for H0 (independence): df=1 fewer parameter
  // BIC for H1 (association): full model
  // BF10 ≈ exp((BIC_H0 - BIC_H1) / 2)

  const p1 = (a + 0.5) / (a + b + 1);
  const p2 = (c + 0.5) / (c + d + 1);
  const p0 = (a + c + 1) / (n + 2);

  // Log-likelihoods
  const ll_h1 =
    (a > 0 ? a * Math.log(p1) : 0) +
    (b > 0 ? b * Math.log(1 - p1) : 0) +
    (c > 0 ? c * Math.log(p2) : 0) +
    (d > 0 ? d * Math.log(1 - p2) : 0);

  const ll_h0 =
    (a + c > 0 ? (a + c) * Math.log(p0) : 0) +
    (b + d > 0 ? (b + d) * Math.log(1 - p0) : 0);

  const bic_h0 = -2 * ll_h0 + 1 * Math.log(n); // 1 parameter
  const bic_h1 = -2 * ll_h1 + 2 * Math.log(n); // 2 parameters

  const bf10 = Math.exp((bic_h0 - bic_h1) / 2);

  return Math.max(0.001, Math.min(1000, bf10)); // clamp to reasonable range
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #42: calibrateWeights
// Bayesian weight calibration with gradient ascent
// ═══════════════════════════════════════════════════════════════

export function calibrateWeights(
  events: EmergenceEvent[],
  currentWeights: Record<string, number>,
  theoreticalWeights: Record<string, number> = THEORETICAL_WEIGHTS,
  options: {
    learningRate?: number;
    maxIterations?: number;
    n0?: number; // virtual observations for Dirichlet prior
    impedanceFactor?: number; // max deviation from theory
  } = {}
): CalibrationResult {
  const {
    learningRate = 0.01,
    maxIterations = 50,
    n0 = 50,
    impedanceFactor = 3,
  } = options;

  const dims = Object.keys(theoreticalWeights);
  let w = { ...currentWeights };

  // Dirichlet prior: α = N₀ * w_theory
  const alpha: Record<string, number> = {};
  dims.forEach((d) => {
    alpha[d] = n0 * (theoreticalWeights[d] || 0.01);
  });

  // Gradient ascent
  for (let iter = 0; iter < maxIterations; iter++) {
    const grad: Record<string, number> = {};
    dims.forEach((d) => (grad[d] = 0));

    // Gradient of log-likelihood
    events.forEach((event) => {
      const snap = event.stateSnapshot;
      const linearComb = dims.reduce(
        (sum, d) => sum + (w[d] || 0) * (snap[d] || 0.5),
        0
      );
      const pEmergence = 1 / (1 + Math.exp(-5 * (linearComb - 0.5)));
      const residual = (event.hadEmergence ? 1 : 0) - pEmergence;

      dims.forEach((d) => {
        grad[d] += residual * (snap[d] || 0.5);
      });
    });

    // Gradient of Dirichlet log-prior
    dims.forEach((d) => {
      grad[d] += (alpha[d] - 1) / Math.max(w[d], 0.001);
    });

    // Update
    dims.forEach((d) => {
      w[d] += (learningRate * grad[d]) / Math.max(events.length, 1);
    });

    // Project to simplex + impedance bounds
    w = applyImpedanceBounds(w, theoreticalWeights, impedanceFactor);
    w = projectToSimplex(w);
  }

  // Safety checks
  const safety = safetyCheck(w, currentWeights, events, theoreticalWeights, impedanceFactor);

  // Compute WAIC
  const waic = computeWAIC(events, w);

  return {
    weights: w,
    epoch: -1, // caller sets this
    waicScore: waic.waic,
    waicSE: waic.se,
    deltaWaic: 0, // caller computes vs previous
    safetyCheck: safety,
    passed: safety.monotonicity && safety.impedance && safety.lyapunov,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #43: projectToSimplex
// Project weight vector onto probability simplex (Σw_i = 1, w_i ≥ 0)
// ═══════════════════════════════════════════════════════════════

export function projectToSimplex(
  w: Record<string, number>
): Record<string, number> {
  const result = { ...w };
  const keys = Object.keys(result);
  // Ensure non-negative
  keys.forEach((k) => {
    result[k] = Math.max(result[k], 0.001);
  });
  // Normalize to sum = 1
  const total = keys.reduce((sum, k) => sum + result[k], 0);
  if (total === 0) {
    // Fallback to uniform
    keys.forEach((k) => (result[k] = 1 / keys.length));
  } else {
    keys.forEach((k) => (result[k] /= total));
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #44: dirichletLogPdf
// Log probability density of Dirichlet distribution
// ═══════════════════════════════════════════════════════════════

export function dirichletLogPdf(
  x: Record<string, number>,
  alpha: Record<string, number>
): number {
  const keys = Object.keys(alpha);
  let logP = 0;

  keys.forEach((k) => {
    const a = alpha[k] || 1;
    const val = Math.max(x[k] || 0.001, 0.001);
    logP += (a - 1) * Math.log(val);
  });

  // Normalization constant (log Beta function via gamma)
  // Omitted for optimization (constant in gradient)
  return logP;
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #45: computeLogLikelihood
// Logistic regression log-likelihood
// ═══════════════════════════════════════════════════════════════

export function computeLogLikelihood(
  events: EmergenceEvent[],
  weights: Record<string, number>
): number {
  const dims = Object.keys(weights);
  let ll = 0;

  events.forEach((event) => {
    const snap = event.stateSnapshot;
    const linearComb = dims.reduce(
      (sum, d) => sum + (weights[d] || 0) * (snap[d] || 0.5),
      0
    );
    const p = 1 / (1 + Math.exp(-5 * (linearComb - 0.5)));
    const pClamped = Math.max(1e-10, Math.min(1 - 1e-10, p));

    if (event.hadEmergence) {
      ll += Math.log(pClamped);
    } else {
      ll += Math.log(1 - pClamped);
    }
  });

  return ll;
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #46: safetyCheck
// Validates calibrated weights against safety invariants
// ═══════════════════════════════════════════════════════════════

export function safetyCheck(
  wNew: Record<string, number>,
  wOld: Record<string, number>,
  events: EmergenceEvent[],
  wTheory: Record<string, number> = THEORETICAL_WEIGHTS,
  impedanceFactor: number = 3
): { monotonicity: boolean; impedance: boolean; lyapunov: boolean } {
  const dims = Object.keys(wTheory);

  // 1. Impedance: w ∈ [w_theory/factor, w_theory*factor]
  const impedance = dims.every((d) => {
    const wt = wTheory[d] || 0.01;
    const w = wNew[d] || 0;
    return w >= wt / impedanceFactor && w <= wt * impedanceFactor;
  });

  // 2. Monotonicity: Higher weighted dims should predict emergence
  // (simplified: top 3 weights should have higher avg values in emergence events)
  const emergenceEvents = events.filter((e) => e.hadEmergence);
  const noEmergenceEvents = events.filter((e) => !e.hadEmergence);

  let monotonicity = true;
  if (emergenceEvents.length >= 5 && noEmergenceEvents.length >= 5) {
    const topDims = dims
      .sort((a, b) => (wNew[b] || 0) - (wNew[a] || 0))
      .slice(0, 3);

    topDims.forEach((d) => {
      const avgE =
        emergenceEvents.reduce((s, e) => s + (e.stateSnapshot[d] || 0.5), 0) /
        emergenceEvents.length;
      const avgN =
        noEmergenceEvents.reduce(
          (s, e) => s + (e.stateSnapshot[d] || 0.5),
          0
        ) / noEmergenceEvents.length;
      if (avgE < avgN - 0.1) monotonicity = false;
    });
  }

  // 3. Lyapunov: ΔΛ_mean should not exceed 0.05
  const lambdaOld = dims.reduce(
    (s, d) => s + (wOld[d] || 0) * 0.5,
    0
  );
  const lambdaNew = dims.reduce(
    (s, d) => s + (wNew[d] || 0) * 0.5,
    0
  );
  const lyapunov = Math.abs(lambdaNew - lambdaOld) <= 0.05;

  return { monotonicity, impedance, lyapunov };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #47: computeH1Stats
// Complete H₁ hypothesis test statistics
// ═══════════════════════════════════════════════════════════════

export function computeH1Stats(events: EmergenceEvent[]): H1Stats {
  // Build 2×2 contingency table
  let a = 0, b = 0, c = 0, d = 0;

  events.forEach((event) => {
    const highERS = event.ersAtEvent > ERS_THRESHOLD;
    if (highERS && event.hadEmergence) a++;
    else if (highERS && !event.hadEmergence) b++;
    else if (!highERS && event.hadEmergence) c++;
    else d++;
  });

  const orResult = computeOddsRatio(a, b, c, d);
  const bf10 = computeBF10(a, b, c, d);
  const n = events.length;

  // Power estimation (simplified for OR = 2.5, alpha = 0.05)
  const power = n >= 352 ? 0.80 : n >= 200 ? 0.65 : n >= 100 ? 0.45 : n / 500;

  return {
    contingencyTable: { a, b, c, d },
    oddsRatio: orResult.or,
    orCI: orResult.ci,
    pValue: orResult.p,
    bf10,
    totalEvents: n,
    power: Math.min(power, 0.99),
    significant: orResult.or > 2.0 && orResult.p < 0.05,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #48: normalCDF
// Standard normal CDF (Abramowitz & Stegun approximation)
// ═══════════════════════════════════════════════════════════════

export function normalCDF(x: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.SQRT2;

  const t = 1.0 / (1.0 + p * x);
  const y =
    1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1.0 + sign * y);
}

// ═══════════════════════════════════════════════════════════════
// HELPER: applyImpedanceBounds
// ═══════════════════════════════════════════════════════════════

function applyImpedanceBounds(
  w: Record<string, number>,
  wTheory: Record<string, number>,
  factor: number
): Record<string, number> {
  const bounded = { ...w };
  Object.keys(wTheory).forEach((d) => {
    const wt = wTheory[d] || 0.01;
    bounded[d] = Math.max(wt / factor, Math.min(wt * factor, bounded[d] || 0));
  });
  return bounded;
}

// ═══════════════════════════════════════════════════════════════
// HELPER: computeWAIC
// Watanabe-Akaike Information Criterion
// ═══════════════════════════════════════════════════════════════

function computeWAIC(
  events: EmergenceEvent[],
  weights: Record<string, number>
): { waic: number; se: number } {
  if (events.length === 0) return { waic: 0, se: 0 };

  const dims = Object.keys(weights);
  const logLiks: number[] = [];

  events.forEach((event) => {
    const snap = event.stateSnapshot;
    const linearComb = dims.reduce(
      (sum, d) => sum + (weights[d] || 0) * (snap[d] || 0.5),
      0
    );
    const p = 1 / (1 + Math.exp(-5 * (linearComb - 0.5)));
    const pClamped = Math.max(1e-10, Math.min(1 - 1e-10, p));
    const ll = event.hadEmergence
      ? Math.log(pClamped)
      : Math.log(1 - pClamped);
    logLiks.push(ll);
  });

  const lppd = logLiks.reduce((s, v) => s + v, 0);
  const mean = lppd / logLiks.length;
  const variance = logLiks.reduce((s, v) => s + Math.pow(v - mean, 2), 0);
  const pWaic = variance; // effective number of parameters

  const waic = -2 * (lppd - pWaic);
  const se = Math.sqrt(
    (logLiks.length / (logLiks.length - 1)) *
      logLiks.reduce((s, v) => s + Math.pow(-2 * v - waic / logLiks.length, 2), 0)
  );

  return { waic, se };
}

// ═══════════════════════════════════════════════════════════════
// HELPER: computeGAF
// Goal Alignment Factor — amplifies emergence probability
// ═══════════════════════════════════════════════════════════════

export function computeGAF(
  lambda: number,
  ers: number,
  alphaK: number = 2.0
): number {
  return Math.max(0.5, Math.min(4.0, 1.0 + (lambda - 0.5) * alphaK * ers));
}

// ═══════════════════════════════════════════════════════════════
// HELPER: computeSNR
// Shannon Signal-to-Noise Ratio
// ═══════════════════════════════════════════════════════════════

export function computeSNR(lambda: number, entropy: number): number {
  const signal = Math.max(0.001, lambda);
  const noise = Math.max(0.001, entropy);
  return Math.log2(signal / noise);
}
